import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Plus, X, MapPin, Trash2 } from "lucide-react";

export default function Locations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/locations");
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this geofence?")) return;
    await api.delete(`/locations/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="locations-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Geofencing</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Office & Farm Locations</h1>
          <p className="text-stone-600 mt-1 text-sm">Geofences against which GPS punch-ins are validated.</p>
        </div>
        <button onClick={() => setShow(true)} data-testid="add-location-button"
                className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
          <Plus className="w-4 h-4" /> Add Location
        </button>
      </header>

      {loading ? <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="locations-grid">
          {items.map((l) => (
            <div key={l.id} className="card-flat" data-testid={`location-${l.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary-light)" }}>
                    <MapPin className="w-5 h-5" style={{ color: "var(--color-primary)" }} />
                  </div>
                  <div>
                    <div className="font-heading text-lg font-bold">{l.name}</div>
                    <div className="text-xs text-stone-500">Radius {l.radius_meters}m</div>
                  </div>
                </div>
                <button onClick={() => remove(l.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 text-xs text-stone-600 font-mono">
                {l.latitude.toFixed(6)}, {l.longitude.toFixed(6)}
              </div>
              <a href={`https://www.google.com/maps?q=${l.latitude},${l.longitude}`} target="_blank" rel="noopener noreferrer"
                 className="mt-2 text-xs font-semibold inline-block" style={{ color: "var(--color-primary)" }}>
                Open in Maps →
              </a>
            </div>
          ))}
        </div>
      )}

      {show && <LocModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function LocModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", latitude: "", longitude: "", radius_meters: 200 });
  const [busy, setBusy] = useState(false);
  const fromBrowser = () => {
    if (!navigator.geolocation) return alert("Geolocation not supported");
    navigator.geolocation.getCurrentPosition((p) => {
      setForm((f) => ({ ...f, latitude: p.coords.latitude.toFixed(6), longitude: p.coords.longitude.toFixed(6) }));
    }, (e) => alert(e.message), { enableHighAccuracy: true });
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.post("/locations", { ...form, latitude: Number(form.latitude), longitude: Number(form.longitude), radius_meters: Number(form.radius_meters) });
    setBusy(false); onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4" data-testid="location-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5"><h2 className="font-heading text-xl font-bold">Add Location</h2><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <form onSubmit={submit} className="space-y-3">
          <In label="Name (e.g., Bengaluru HQ)" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <div className="grid grid-cols-2 gap-3">
            <In label="Latitude" value={form.latitude} onChange={(v) => setForm({ ...form, latitude: v })} required />
            <In label="Longitude" value={form.longitude} onChange={(v) => setForm({ ...form, longitude: v })} required />
          </div>
          <In label="Radius (meters)" type="number" value={form.radius_meters} onChange={(v) => setForm({ ...form, radius_meters: v })} />
          <button type="button" onClick={fromBrowser} data-testid="use-current-location"
                  className="w-full py-2 rounded-md border border-stone-300 text-sm font-medium hover:bg-stone-50">
            Use my current location
          </button>
          <button type="submit" disabled={busy} data-testid="save-location-button"
                  className="w-full py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}
function In({ label, type = "text", value, onChange, required }) {
  return <label><div className="kpi-label mb-1.5">{label}</div>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
           className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
  </label>;
}
